import { useCallback, useRef } from 'react';

import { type UseTrackCreditMeterViewedParams, useTrackCreditMeterViewed } from '@air/analytics';
import { FIVE_MINUTES } from '@air/utilities';

import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';

export type UseCreditMeterViewTrackingParams = {
  location: UseTrackCreditMeterViewedParams['location'];
};

export type UseCreditMeterViewTrackingResult = {
  onMeterOpenChange: (open: boolean) => void;
};

export const useCreditMeterViewTracking = ({
  location,
}: UseCreditMeterViewTrackingParams): UseCreditMeterViewTrackingResult => {
  const { dataUpdatedAt, refetch } = useCreditStats();
  const { trackCreditMeterViewed } = useTrackCreditMeterViewed();
  const creditAnalyticsParams = useCreditAnalyticsParams();
  const hasTrackedView = useRef<boolean>();

  const onMeterOpenChange = useCallback(
    (open: boolean) => {
      // Track only first view per session to avoid noisy analytics from repeated opens.
      if (open && creditAnalyticsParams && !hasTrackedView.current) {
        hasTrackedView.current = true;
        trackCreditMeterViewed({
          location,
          trigger: 'click',
          ...creditAnalyticsParams,
        });
      }

      // Refetch data on user interaction (if older than 5 minutes).
      if (open && Date.now() - dataUpdatedAt > FIVE_MINUTES) {
        refetch();
      }
    },
    [creditAnalyticsParams, dataUpdatedAt, location, refetch, trackCreditMeterViewed],
  );

  return { onMeterOpenChange };
};
